/** 邀请家人（与 iOS Me/ChildEditSheet.swift 的 InviteSheet 一致）：打开时生成邀请码，可复制邀请码 / 邀请文字 */
import { Copy, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, useCalendar, useStore } from "../../core";
import { Button, Sheet, SheetBody, SheetHeader, Spinner } from "../../ui";
import { copyText } from "./util";

export function InviteSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} width={440} label="邀请家人">
      <SheetHeader title="邀请家人" onClose={onClose} />
      <InviteBody />
    </Sheet>
  );
}

function InviteBody() {
  const cal = useCalendar();
  const familyName = useStore((s) => s.family?.name ?? "我们家");
  const [invite, setInvite] = useState<{ code: string; expiresAt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .post<{ code: string; expiresAt: number }>("family/invitations")
      .then((r) => alive && setInvite(r))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [attempt]);

  const message = invite ? `邀请你加入「${familyName}」：在 kids-planner 家长端注册时填写邀请码 ${invite.code}` : "";
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <SheetBody gap={16}>
      <p className="me-note me-note--lg">家人在家长端注册时填写邀请码，就能加入这个家庭，一起管理孩子的安排。</p>
      {invite ? (
        <div className="me-invite">
          <div className="me-invite-code kp-num" aria-label="邀请码">
            {invite.code}
          </div>
          <small>
            {cal.relativeDay(cal.dateString(invite.expiresAt))} {cal.timeString(invite.expiresAt)} 前有效
          </small>
          <div className="me-invite-actions">
            <Button variant="dark" height={48} icon={<Copy size={18} />} onClick={() => void copyText(invite.code, "邀请码已复制")}>
              复制邀请码
            </Button>
            {canShare ? (
              <Button variant="soft" height={48} icon={<Share2 size={18} />} onClick={() => void navigator.share({ text: message }).catch(() => undefined)}>
                分享
              </Button>
            ) : (
              <Button variant="soft" height={48} onClick={() => void copyText(message, "邀请文字已复制")}>
                复制邀请文字
              </Button>
            )}
          </div>
        </div>
      ) : error ? (
        <div className="me-invite">
          <p className="me-error">{error}</p>
          <Button variant="soft" height={44} onClick={() => setAttempt((n) => n + 1)}>
            重试
          </Button>
        </div>
      ) : (
        <div className="me-invite">
          <Spinner size={24} />
        </div>
      )}
    </SheetBody>
  );
}
